'use client'
import { useEffect, useState, useRef, useCallback } from 'react'
import RankingHeader from 'components/RankingHeader'
import RankingBody from 'components/RankingBody'
import RadioFilter from 'components/RadioFilter'
import SelectFilter from 'components/SelectFilter'
import Search from 'components/Search'
import { ONE } from 'config/apis'
import { provinces } from 'config/const'
import Spin from 'components/Spin'
import { getRandomInt } from 'utils/maths'
import NoResults from 'components/NoResults'

const genderFilter = [
  {
    value: 'M',
    title: 'Masculí'
  },
  {
    value: 'F',
    title: 'Femení'
  }
]
const defaultGender = genderFilter[getRandomInt(2)].value

const itemsToOptions = (items, onlyKeys, hasLocation) => {
  return onlyKeys
    ? items.map((item) => ({
        title: item,
        value: item,
        hasLocation: hasLocation
      }))
    : items.map((item) => ({
        title: item.Name.trim(),
        value: item.Name.trim(),
        hasLocation: item.UseDelegation === '1'
      }))
}

const Ranking = ({ rankingFilters, rankingSelected, categoryHasLocation }) => {
  const defaultRankingName = rankingSelected || 'Absolut'
  const [loading, setLoading] = useState(true)
  const [genderFilterSelected, setGenderFilterSelected] =
    useState(defaultGender)
  const [rankingFilterSelected, setRankingFilterSelected] =
    useState(defaultRankingName)
  const [placeFilterSelected, setPlaceFilterSelected] = useState('')
  const [torsList, setTorsList] = useState(
    rankingFilters
      ? itemsToOptions(rankingFilters, true, categoryHasLocation)
      : []
  )
  const [torRanking, setTorRanking] = useState([])
  const [heroPlayer, setHeroPlayer] = useState()
  const [page, setPage] = useState(1)
  const [showLocation, setShowLocation] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [searchName, setSearchName] = useState('')
  const [searchLicense, setSearchLicense] = useState('')
  const observer = useRef()
  const limit = 48

  const getTorsList = async () => {
    try {
      const req = await fetch(ONE.RANKING_LIST, ONE.HEADERS)
      const list = await req.json()
      setTorsList(itemsToOptions(list.Data))
      setRankingFilterSelected(defaultRankingName)
    } catch (err) {
      console.log(err)
    }
  }

  const getTorRanking = async () => {
    if (torsList.length) {
      setLoading(true)
      const torHasLocation = torsList.find(
        ({ value }) => value === rankingFilterSelected
      )?.hasLocation
      setShowLocation(torHasLocation)
      try {
        const res = await fetch(
          `${ONE.RANKING}?rankingName=${rankingFilterSelected}&gender=${genderFilterSelected}&delegation=${placeFilterSelected}&page=${page}&limit=${limit}&license=${searchLicense}&name=${searchName}`,
          ONE.HEADERS
        )
        const tors = await res.json()
        if (tors?.Data.length) {
          setHasMore(tors.Data.length === limit)
          if (page === 1) {
            setTorRanking(tors.Data)
            setHeroPlayer(tors.Data[Math.floor(Math.random() * 10) + 1])
          } else {
            setTorRanking((prev) =>
              [...new Set([...prev, ...tors.Data])].sort(
                (a, b) => a.Ranking - b.Ranking
              )
            )
          }
        }
      } catch (err) {
        console.log(err)
      }
      setLoading(false)
    }
  }

  const rankingTail = useCallback(
    (node) => {
      if (loading) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && page < 20) {
          setPage((prevPage) => prevPage + 1)
        }
      })
      if (node) observer.current.observe(node)
    },
    [loading]
  )

  useEffect(() => {
    if (!rankingFilters) {
      getTorsList()
    }
  }, [])

  useEffect(() => {
    setPage(1)
    setTorRanking([])
    getTorRanking()
  }, [
    rankingFilterSelected,
    genderFilterSelected,
    placeFilterSelected,
    searchName,
    searchLicense
  ])

  useEffect(() => {
    getTorRanking()
  }, [torsList, page])

  return (
    <>
      {heroPlayer && (
        <RankingHeader
          key="0"
          name={heroPlayer?.Name}
          ranking={heroPlayer?.Ranking}
          score={heroPlayer?.Point}
          photo={heroPlayer?.Image}
        />
      )}
      <div className="max-w-7xl mx-auto">
        <div className="lg:flex mt-16 mx-2">
          <RadioFilter
            filters={genderFilter}
            radioSelected={genderFilterSelected}
            setRadioSelected={setGenderFilterSelected}
          />
          <SelectFilter
            filters={torsList}
            defaultValue={defaultRankingName}
            setSelected={setRankingFilterSelected}
            className="lg:w-[275px] lg:ml-3 mt-2 lg:mt-0"
          />
          {showLocation ? (
            <SelectFilter
              text="Provincia"
              filters={provinces}
              selected={placeFilterSelected}
              setSelected={setPlaceFilterSelected}
              className="lg:w-[200px] lg:ml-3 mt-2 lg:mt-0"
            />
          ) : null}
        </div>
        <div className="mt-8 mb-16 mx-2">
          <Search
            setSearchLicense={setSearchLicense}
            setSearchName={setSearchName}
          />
        </div>
      </div>

      <div className="mb-60">
        {!loading && !torRanking.length ? (
          <div className="max-w-7xl mx-auto pl-5">
            <NoResults />
          </div>
        ) : (
          <>
            <RankingBody ranking={torRanking} setHeroPlayer={setHeroPlayer} />
            <div ref={rankingTail}></div>
          </>
        )}
        {loading && (
          <div className="flex justify-center">
            <Spin color="text-blue" size="10" className="my-2 lg:my-8" />
          </div>
        )}
      </div>
    </>
  )
}

export default Ranking
